// v3.3.0 - 2026-02-02 - Added multi-period filter

import { useCallback, useMemo, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { useAuthStore } from '@/lib/stores/authStore'
import { useQuery } from '@/lib/hooks'
import { cer } from '@/lib/api/client'
import { PageContainer, PageHeader } from '@/components/layout'
import { Card, CardContent, CardHeader, CardTitle, Badge, Button, Spinner } from '@/components/ui'

function Avatar({ name, size = 'md' }: { name: string; size?: 'sm' | 'md' | 'lg' }) {
  const initials = name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  const sizes = {
    sm: 'w-8 h-8 text-xs',
    md: 'w-12 h-12 text-base',
    lg: 'w-16 h-16 text-xl',
  }

  return (
    <div
      className={`${sizes[size]} rounded-full bg-primary/10 text-primary font-semibold flex items-center justify-center`}
    >
      {initials}
    </div>
  )
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between py-2 border-b border-border last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  )
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
  }).format(value)
}

function formatKwh(value: number): string {
  return new Intl.NumberFormat('it-IT', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value) + ' kWh'
}

function formatPeriod(period: string): string {
  if (period.includes('/')) {
    const [year, range] = period.split('-')
    const [startMonth, endMonth] = range.split('/')
    const monthNames = [
      'Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu',
      'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'
    ]
    return `${monthNames[parseInt(startMonth) - 1]}-${monthNames[parseInt(endMonth) - 1]} ${year}`
  }
  const [year, month] = period.split('-')
  const monthNames = [
    'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
    'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
  ]
  return `${monthNames[parseInt(month) - 1]} ${year}`
}

function getTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    producer: 'Produttore',
    consumer: 'Consumatore',
    prosumer: 'Prosumer',
  }
  return labels[type] || type
}

function getTypeBadgeVariant(type: string): 'default' | 'success' | 'warning' {
  const variants: Record<string, 'default' | 'success' | 'warning'> = {
    producer: 'success',
    consumer: 'default',
    prosumer: 'warning',
  }
  return variants[type] || 'default'
}

function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    active: 'Attivo',
    inactive: 'Inattivo',
    pending: 'In Attesa',
    draft: 'Bozza',
    approved: 'Approvato',
    distributed: 'Distribuito',
  }
  return labels[status] || status
}

function getStatusBadgeVariant(status: string): 'default' | 'success' | 'warning' | 'error' | 'info' {
  const variants: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
    active: 'success',
    inactive: 'error',
    pending: 'warning',
    draft: 'default',
    approved: 'success',
    distributed: 'info',
  }
  return variants[status] || 'default'
}

// Period filter component
function PeriodFilter({
  periods,
  selected,
  onChange
}: {
  periods: string[]
  selected: string[]
  onChange: (periods: string[]) => void
}) {
  const togglePeriod = (period: string) => {
    if (selected.includes(period)) {
      onChange(selected.filter(p => p !== period))
    } else {
      onChange([...selected, period])
    }
  }

  const selectAll = () => onChange([...periods])
  const clearAll = () => onChange([])

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm text-muted-foreground mr-2">Periodi:</span>
      <Button variant="ghost" size="sm" onClick={selectAll} className="text-xs">
        Tutti
      </Button>
      <Button variant="ghost" size="sm" onClick={clearAll} className="text-xs">
        Nessuno
      </Button>
      <div className="w-px h-4 bg-border mx-1" />
      {periods.map(period => (
        <button
          key={period}
          onClick={() => togglePeriod(period)}
          className={`px-2 py-1 text-xs rounded-md transition-colors ${
            selected.includes(period)
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
        >
          {formatPeriod(period)}
        </button>
      ))}
    </div>
  )
}

export function MemberDetail() {
  const { memberId } = useParams<{ memberId: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()

  // Period filter state
  const [selectedPeriods, setSelectedPeriods] = useState<string[]>([])

  const fetchMemberDetail = useCallback(async () => {
    if (!user?.cer_id || !memberId) return null
    return cer.getMemberDetail(user.cer_id, memberId)
  }, [user?.cer_id, memberId])

  const { data, isLoading, error } = useQuery(fetchMemberDetail, {
    enabled: !!user?.cer_id && !!memberId,
  })

  // Extract available periods from tiHistory
  const availablePeriods = useMemo(() => {
    if (!data?.tiHistory || data.tiHistory.length === 0) return []
    return data.tiHistory
      .map(entry => entry.period)
      .reverse() // Oldest first
  }, [data?.tiHistory])

  // Initialize selected periods to all available (on first load)
  useMemo(() => {
    if (selectedPeriods.length === 0 && availablePeriods.length > 0) {
      setSelectedPeriods(availablePeriods)
    }
  }, [availablePeriods, selectedPeriods.length])

  // Filter tiHistory based on selected periods
  const filteredTiHistory = useMemo(() => {
    if (!data?.tiHistory) return []
    if (selectedPeriods.length === 0) return data.tiHistory
    return data.tiHistory.filter(entry => selectedPeriods.includes(entry.period))
  }, [data?.tiHistory, selectedPeriods])

  // Prepare chart data from filtered tiHistory
  const chartData = useMemo(() => {
    if (filteredTiHistory.length === 0) return []

    return filteredTiHistory
      .slice()
      .reverse() // Oldest first for chart
      .map((entry) => {
        const [year, month] = entry.period.split('-')
        const monthNames = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic']
        const label = entry.period.includes('/')
          ? entry.period
          : `${monthNames[parseInt(month) - 1]} ${year.slice(2)}`

        return {
          period: label,
          immessa: entry.immessaKwh || 0,
          prelevata: entry.prelevataKwh || 0,
          eaci: entry.eaciKwh,
          ti: entry.tiQuota,
        }
      })
  }, [filteredTiHistory])

  if (isLoading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      </PageContainer>
    )
  }

  if (error) {
    return (
      <PageContainer>
        <div className="rounded-md bg-red-50 p-4 text-sm text-red-600">
          Errore nel caricamento del membro: {error}
        </div>
        <Button variant="outline" onClick={() => navigate('/admin/members')} className="mt-4">
          Torna ai Membri
        </Button>
      </PageContainer>
    )
  }

  if (!data) {
    return (
      <PageContainer>
        <div className="rounded-md bg-yellow-50 p-4 text-sm text-yellow-700">
          Membro non trovato
        </div>
        <Button variant="outline" onClick={() => navigate('/admin/members')} className="mt-4">
          Torna ai Membri
        </Button>
      </PageContainer>
    )
  }

  const { member, energy, tiHistory } = data

  return (
    <PageContainer>
      <PageHeader
        title="Dettaglio Membro"
        description="Visualizza e gestisci i dati del membro"
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/admin/members')}>
              <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Indietro
            </Button>
            <Button onClick={() => navigate(`/admin/members?edit=${memberId}`)}>
              <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
              Modifica
            </Button>
          </div>
        }
      />

      <div className="grid gap-6 md:grid-cols-3">
        {/* Profile Card */}
        <Card className="md:col-span-1">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center">
              <Avatar name={member.name} size="lg" />
              <h2 className="mt-4 text-xl font-semibold">{member.name}</h2>
              <p className="text-muted-foreground">{member.email}</p>
              <div className="flex gap-2 mt-3">
                <Badge variant={getTypeBadgeVariant(member.type)}>
                  {getTypeLabel(member.type)}
                </Badge>
                <Badge variant={getStatusBadgeVariant(member.status)}>
                  {getStatusLabel(member.status)}
                </Badge>
              </div>
              {member.role === 'admin' && (
                <Badge variant="default" className="mt-2">
                  Amministratore
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Member Info Card */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>Informazioni Membro</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-x-8">
              <div>
                <InfoRow label="POD" value={<span className="font-mono text-sm">{member.pod || '-'}</span>} />
                <InfoRow label="Codice Fiscale" value={<span className="font-mono text-sm">{member.cf || '-'}</span>} />
                <InfoRow label="CENSIMP" value={<span className="font-mono text-sm">{member.censimp || '-'}</span>} />
              </div>
              <div>
                <InfoRow label="Tipologia" value={member.isCompany ? 'Impresa' : 'Privato'} />
                <InfoRow label="Ruolo CER" value={member.role === 'admin' ? 'Amministratore' : 'Membro'} />
                <InfoRow
                  label="% PNRR"
                  value={
                    member.pnrrPercent > 0 ? (
                      <Badge variant="default">{member.pnrrPercent}%</Badge>
                    ) : (
                      '-'
                    )
                  }
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Energy Data Card */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>Dati Energetici</CardTitle>
          </CardHeader>
          <CardContent>
            {energy.lastPeriod ? (
              <>
                <p className="text-sm text-muted-foreground mb-4">
                  Totali cumulati - Ultimo periodo: {formatPeriod(energy.lastPeriod)}
                </p>
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 rounded-lg bg-green-50 border border-green-100">
                    <p className="text-sm text-green-700">Energia Immessa</p>
                    <p className="text-xl font-semibold text-green-800 mt-1">
                      {formatKwh(energy.totalImmessa)}
                    </p>
                  </div>
                  <div className="p-4 rounded-lg bg-blue-50 border border-blue-100">
                    <p className="text-sm text-blue-700">Energia Prelevata</p>
                    <p className="text-xl font-semibold text-blue-800 mt-1">
                      {formatKwh(energy.totalPrelevata)}
                    </p>
                  </div>
                  <div className="p-4 rounded-lg bg-amber-50 border border-amber-100">
                    <p className="text-sm text-amber-700">EACI Condivisa</p>
                    <p className="text-xl font-semibold text-amber-800 mt-1">
                      {formatKwh(energy.totalEaci)}
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <p className="text-muted-foreground">Nessun dato energetico disponibile</p>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="md:col-span-1">
          <CardHeader>
            <CardTitle>Azioni Rapide</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => navigate(`/admin/energy-data?memberId=${memberId}`)}
            >
              <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
              </svg>
              Browser Dati Energia
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => navigate(`/admin/members?edit=${memberId}`)}
            >
              <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Modifica Membro
            </Button>
          </CardContent>
        </Card>

        {/* Energy Trend Chart with Period Filter */}
        {availablePeriods.length > 0 && (
          <Card className="md:col-span-3">
            <CardHeader>
              <CardTitle>Andamento Energia e TI</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <PeriodFilter
                periods={availablePeriods}
                selected={selectedPeriods}
                onChange={setSelectedPeriods}
              />
              {chartData.length > 0 ? (
                <div className="h-[350px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={chartData}>
                      <defs>
                        <linearGradient id="colorImmessa" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#22c55e" stopOpacity={0.05} />
                        </linearGradient>
                        <linearGradient id="colorPrelevata" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.05} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" fontSize={12} />
                      <YAxis
                        yAxisId="left"
                        fontSize={12}
                        tickFormatter={(value) => `${value} kWh`}
                      />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        fontSize={12}
                        tickFormatter={(value) => `€${value}`}
                      />
                      <Tooltip
                        formatter={(value: number, name: string) => {
                          if (name === 'TI') return [`€${value.toFixed(2)}`, 'TI']
                          return [`${value.toFixed(2)} kWh`, name]
                        }}
                      />
                      <Legend />
                      <Area
                        yAxisId="left"
                        type="monotone"
                        dataKey="immessa"
                        name="Immessa"
                        stroke="#22c55e"
                        strokeWidth={2}
                        fill="url(#colorImmessa)"
                      />
                      <Area
                        yAxisId="left"
                        type="monotone"
                        dataKey="prelevata"
                        name="Prelevata"
                        stroke="#3b82f6"
                        strokeWidth={2}
                        fill="url(#colorPrelevata)"
                      />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="eaci"
                        name="EACI"
                        stroke="#f59e0b"
                        strokeWidth={2}
                        dot={{ r: 4 }}
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="ti"
                        name="TI"
                        stroke="#8b5cf6"
                        strokeWidth={2}
                        dot={{ r: 4 }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  Seleziona almeno un periodo per visualizzare il grafico
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* TI History Card - shows filtered data */}
        <Card className="md:col-span-1">
          <CardHeader>
            <CardTitle>Storico Quote TI</CardTitle>
          </CardHeader>
          <CardContent>
            {filteredTiHistory.length > 0 ? (
              <div className="space-y-3">
                {filteredTiHistory.slice(0, 10).map((entry) => (
                  <Link
                    key={`${entry.calculationId}-${entry.period}`}
                    to={`/admin/calculations/${entry.calculationId}`}
                    className="block p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{formatPeriod(entry.period)}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant={getStatusBadgeVariant(entry.status)} className="text-xs">
                            {getStatusLabel(entry.status)}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            EACI: {formatKwh(entry.eaciKwh)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-right">
                          <p className="text-lg font-semibold">{formatCurrency(entry.tiQuota)}</p>
                          {entry.eccedenzaQuota > 0 && (
                            <p className="text-xs text-green-600">+{formatCurrency(entry.eccedenzaQuota)} ecc.</p>
                          )}
                        </div>
                        <svg className="h-4 w-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </Link>
                ))}
                {filteredTiHistory.length > 10 && (
                  <p className="text-sm text-muted-foreground text-center pt-2">
                    +{filteredTiHistory.length - 10} altri periodi
                  </p>
                )}
              </div>
            ) : (
              <p className="text-muted-foreground">
                {tiHistory.length > 0 ? 'Seleziona periodi per vedere le quote TI' : 'Nessun calcolo TI registrato'}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  )
}

export default MemberDetail
